/**
 * Erlang C formula implementation for contact center staffing calculations
 * Based on queueing theory to calculate required staff and service levels
 */

/**
 * Calculate factorial (with memoization for performance)
 */
const factorialCache: { [key: number]: number } = {};
function factorial(n: number): number {
  if (n === 0 || n === 1) return 1;
  if (factorialCache[n]) return factorialCache[n];

  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  factorialCache[n] = result;
  return result;
}

/**
 * Calculate traffic intensity (Erlangs)
 * @param callsPerInterval - Number of calls expected in the interval
 * @param aht - Average Handle Time in minutes
 * @param intervalMinutes - Interval length in minutes (default 15)
 * @returns Traffic intensity in Erlangs
 */
export function calculateTrafficIntensity(
  callsPerInterval: number,
  aht: number,
  intervalMinutes: number = 15
): number {
  // Convert to hourly rate
  const callsPerHour = (callsPerInterval / intervalMinutes) * 60;
  const ahtHours = aht / 60;
  return callsPerHour * ahtHours;
}

/**
 * Erlang C formula - calculate probability that a call will wait
 * @param agents - Number of agents
 * @param traffic - Traffic intensity (Erlangs)
 * @returns Probability of waiting (0-1)
 */
export function erlangC(agents: number, traffic: number): number {
  if (agents <= 0 || traffic <= 0) return 0;
  if (traffic >= agents) return 1; // Unstable - more traffic than capacity

  let sum = 0;
  for (let k = 0; k < agents; k++) {
    sum += Math.pow(traffic, k) / factorial(k);
  }

  const numerator = Math.pow(traffic, agents) / factorial(agents);
  const denominator = numerator * (agents / (agents - traffic)) + sum;

  return numerator / denominator;
}

/**
 * Calculate service level (% answered within target time)
 * @param agents - Number of agents
 * @param traffic - Traffic intensity (Erlangs)
 * @param aht - Average Handle Time in seconds
 * @param targetAnswerTime - Target answer time in seconds
 * @returns Service level as percentage (0-100)
 */
export function calculateServiceLevel(
  agents: number,
  traffic: number,
  aht: number,
  targetAnswerTime: number
): number {
  if (agents <= 0 || traffic <= 0) return 100;
  if (traffic >= agents) return 0; // Unstable queue

  const pw = erlangC(agents, traffic);
  const exponent = -(agents - traffic) * (targetAnswerTime / aht);
  const serviceLevel = (1 - pw * Math.exp(exponent)) * 100;

  return Math.max(0, Math.min(100, serviceLevel));
}

/**
 * Calculate required agents to meet service level target
 * @param traffic - Traffic intensity (Erlangs)
 * @param targetSL - Target service level (0-100)
 * @param aht - Average Handle Time in seconds
 * @param targetAnswerTime - Target answer time in seconds
 * @param maxAgents - Maximum agents to try (default 200)
 * @returns Required number of agents
 */
export function calculateRequiredAgents(
  traffic: number,
  targetSL: number,
  aht: number,
  targetAnswerTime: number,
  maxAgents: number = 200
): number {
  if (traffic <= 0) return 0;

  // Start with minimum possible agents (traffic rounded up)
  let minAgents = Math.ceil(traffic);

  // Binary search for optimal agent count
  let low = minAgents;
  let high = maxAgents;
  let requiredAgents = high;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const sl = calculateServiceLevel(mid, traffic, aht, targetAnswerTime);

    if (sl >= targetSL) {
      requiredAgents = mid;
      high = mid - 1; // Try with fewer agents
    } else {
      low = mid + 1; // Need more agents
    }
  }

  return requiredAgents;
}

/**
 * Calculate average speed of answer (ASA)
 * @param agents - Number of agents
 * @param traffic - Traffic intensity (Erlangs)
 * @param aht - Average Handle Time in seconds
 * @returns Average speed of answer in seconds
 */
export function calculateASA(
  agents: number,
  traffic: number,
  aht: number
): number {
  if (agents <= 0 || traffic <= 0 || traffic >= agents) return Infinity;

  const pw = erlangC(agents, traffic);
  const asa = (pw * aht) / (agents - traffic);

  return asa;
}

/**
 * Calculate occupancy (agent utilization)
 * @param traffic - Traffic intensity (Erlangs)
 * @param agents - Number of agents
 * @returns Occupancy as percentage (0-100)
 */
export function calculateOccupancy(traffic: number, agents: number): number {
  if (agents <= 0) return 0;
  return Math.min(100, (traffic / agents) * 100);
}

/**
 * Adjust required agents for shrinkage
 * @param requiredAgents - Raw required agents from Erlang C
 * @param shrinkagePercent - Shrinkage percentage (0-100)
 * @returns Adjusted required agents accounting for shrinkage
 */
export function adjustForShrinkage(
  requiredAgents: number,
  shrinkagePercent: number
): number {
  const shrinkageFactor = 1 + (shrinkagePercent / 100);
  return Math.ceil(requiredAgents * shrinkageFactor);
}

/**
 * Complete staffing calculation with all parameters
 * @param callVolume - Expected call volume in the interval
 * @param ahtMinutes - Average Handle Time in minutes
 * @param targetSLPercent - Target service level percentage (e.g., 80)
 * @param targetAnswerSeconds - Target answer time in seconds (e.g., 20)
 * @param shrinkagePercent - Shrinkage percentage (e.g., 20)
 * @param intervalMinutes - Interval length in minutes (default 15)
 * @returns Staffing calculation results
 */
export function calculateStaffing(params: {
  callVolume: number;
  ahtMinutes: number;
  targetSLPercent: number;
  targetAnswerSeconds: number;
  shrinkagePercent: number;
  intervalMinutes?: number;
}) {
  const {
    callVolume,
    ahtMinutes,
    targetSLPercent,
    targetAnswerSeconds,
    shrinkagePercent,
    intervalMinutes = 15,
  } = params;

  // Convert AHT to seconds for Erlang C
  const ahtSeconds = ahtMinutes * 60;

  // Calculate traffic intensity
  const traffic = calculateTrafficIntensity(callVolume, ahtMinutes, intervalMinutes);

  // Calculate raw required agents
  const rawRequired = calculateRequiredAgents(
    traffic,
    targetSLPercent,
    ahtSeconds,
    targetAnswerSeconds
  );

  // Adjust for shrinkage
  const adjustedRequired = adjustForShrinkage(rawRequired, shrinkagePercent);

  // Calculate actual service level with required agents
  const achievedSL = calculateServiceLevel(
    adjustedRequired,
    traffic,
    ahtSeconds,
    targetAnswerSeconds
  );

  // Calculate occupancy
  const occupancy = calculateOccupancy(traffic, adjustedRequired);

  // Calculate ASA
  const asa = calculateASA(adjustedRequired, traffic, ahtSeconds);

  return {
    requiredAgents: adjustedRequired,
    rawRequired,
    traffic,
    achievedServiceLevel: Math.round(achievedSL * 10) / 10, // Round to 1 decimal
    occupancy: Math.round(occupancy * 10) / 10,
    averageSpeedOfAnswer: Math.round(asa * 10) / 10,
  };
}
